import { useNavigate } from "@tanstack/react-router";
import type { Row, Table } from "@tanstack/react-table";
import type { VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import {
  AlertTriangle,
  EllipsisVertical,
  ExternalLink,
  MessagesSquare,
  Paperclip,
} from "lucide-react";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useTenant } from "@/providers/session";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { CommentsCount } from "@/components/shared/invoice-comments/comments-count";
import { WithComments } from "@/components/shared/invoice-comments/with-comments";
import { PrefetchPdfOnHover } from "@/components/shared/pdf-tools/components/prefetch-pdf-on-hover";
import { cn } from "@/lib/utils";

type Props = {
  row: Row<any>;
  table: Table<any>;
  virtualRow: VirtualItem;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  onNotABooking: (invoiceId: string, lineItemIds: string[]) => void;
  setInfoPanelOpened: (value: boolean) => Promise<any>;
  tableContainerWidth: number;
};

export const InvoiceRow = ({
  row,
  table,
  virtualRow,
  onNotABooking,
  rowVirtualizer,
  setInfoPanelOpened,
  tableContainerWidth,
}: Props) => {
  const invoice = row.original.invoice;
  const recipientTag = row.original.tags?.find(
    (tag: any) => tag.tagName === "Adressat Prüfen",
  );
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companySlug } = useTenant();

  const showRowActions = isHovered || table.getFocusedRowId() === row.id;

  const togglePanel = async () => {
    if (table.getFocusedRowId() === row.id) {
      await setInfoPanelOpened(false);
    }
    table.setFocusedRow(
      table.getFocusedRowId() === row.id ? undefined : row.id,
    );
  };

  const isPanelOpen = table.getFocusedRowId() === row.id;

  const handleNotBooking = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
    const lineItemIds = row.original.subRows.flatMap((booking: any) =>
      booking.subRows.map((lineItem: any) => lineItem.id),
    );
    onNotABooking(invoice?.invoiceId, lineItemIds);
  };

  const handleOpenInvoice = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigate({
      to: "/$company/buchhaltung/rechnung/$id",
      params: { company: companySlug, id: invoice?.invoiceId },
    });
  };

  return (
    <PrefetchPdfOnHover invoiceId={row.id}>
      <TableRow
        data-index={virtualRow.index} //needed for dynamic row height measurement
        ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
        key={row.id}
        className={cn(
          "bg-gray-50 px-2 hover:bg-blue-100 cursor-pointer",
          table?.getFocusedRowId?.() === row.id && "bg-blue-200",
        )}
        style={{
          display: "flex",
          alignItems: "center",
          position: "absolute",
          transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
          width: "100%",
          minHeight: "50px",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <div
            className="sticky left-2 right-2 z-20"
            style={{ width: tableContainerWidth }}
          >
            <div
              className="flex flex-1 border-0 items-center gap-2"
              onClick={togglePanel}
            >
              <Badge
                variant="outline"
                className="bg-background"
                style={{ fontWeight: "normal" }}
              >
                {`Beleg #${invoice?.invoiceNo}`}
              </Badge>
              {recipientTag && (
                <Badge
                  variant="outline"
                  className="gap-1 cursor-pointer hover:bg-orange-100 transition-colors"
                  style={{
                    fontWeight: "normal",
                    backgroundColor: "#FFF7ED",
                    color: "#000000",
                    borderColor: "#FB923C",
                  }}
                  onClick={handleOpenInvoice}
                >
                  <AlertTriangle size={12} style={{ color: "#FB923C" }} />
                  {recipientTag.tagName}
                  <ExternalLink size={12} style={{ color: "#FB923C" }} />
                </Badge>
              )}
              <WithComments invoiceId={invoice?.invoiceId}>
                <Badge
                  className="gap-1 text-xs px-2 py-0.5 h-6 bg-background"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <MessagesSquare size={12} />
                  <p className="text-xs" style={{ fontWeight: "normal" }}>
                    {t("comments")}
                  </p>
                  <CommentsCount invoiceId={invoice?.invoiceId} size="sm" />
                </Badge>
              </WithComments>
              <div className="flex-1" />
              <div
                className={`flex space-x-2 transition-opacity duration-200 ${
                  showRowActions ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="flex justify-center gap-1">
                  <Button variant="ghost" size="sm" className="gap-2">
                    {isPanelOpen ? (
                      <PanelRightClose className="w-4 h-4" />
                    ) : (
                      <PanelRightOpen className="w-4 h-4" />
                    )}
                    {t(isPanelOpen ? "close" : "view_invoice")}
                  </Button>
                </div>
              </div>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
            </div>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuGroup>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      onSelect={handleNotBooking}
                      className="cursor-pointer gap-2"
                    >
                      <Paperclip className="h-4 w-4" />
                      <span>{t("no_booking.label")}</span>
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent>{t("no_booking.tooltip")}</TooltipContent>
                </Tooltip>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </div>
        </DropdownMenu>
      </TableRow>
    </PrefetchPdfOnHover>
  );
};
